'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { AnimatePresence, motion } from 'framer-motion';
import { Icon } from './Icon';
import { trackEvent } from '@/lib/analytics';

export interface Language {
  id: string;
  name: string;
}

interface LanguageSelectorProps {
  variant?: 'header' | 'footer' | 'mobile';
  languages: Language[];
}

export default function LanguageSelector({ variant = 'header', languages = [] }: LanguageSelectorProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLangId = pathname.split('/')[1] || 'en';
  const currentLang = languages.find(l => l.id === currentLangId) || languages[0];
  const validLocales = languages.map(l => l.id);
  const currentPathLocale = pathname.split('/')[1];
  const activeLangId = validLocales.includes(currentPathLocale) ? currentPathLocale : 'en';

  const switchLanguage = (targetId: string) => {
    trackEvent('language_switch', {
      from_language: activeLangId,
      to_language: targetId,
    });

    const segments = pathname.split('/').filter(Boolean);

    if (validLocales.includes(segments[0])) {
      segments.shift();
    }

    const prefix = targetId === 'en' ? '' : `/${targetId}`;
    router.push(`${prefix}/${segments.join('/')}` || '/');
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // 1. FOOTER DESIGN: Pill style
  if (variant === 'footer') {
    return (
      <div className="relative group" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          aria-expanded={isDropdownOpen}
          aria-haspopup="listbox"
          aria-label={`Select language, current: ${activeLangId.toUpperCase()}`}
          className="bg-white px-5 py-2 rounded-full flex items-center gap-3 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors"
        >
          <Icon name="Globe" size={18} className="text-brand-primary" />
          <span className="text-sm font-bold text-brand-primary">
            {activeLangId.toUpperCase()}
          </span>
          <Icon name="ChevronDown" size={12} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {isDropdownOpen && (
            <motion.ul
              role="listbox"
              aria-label="Select language"
              initial={{ opacity: 0, y: -4, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.95 }}
              transition={{ duration: 0.15, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="absolute top-full left-0 mt-1 mb-2 bg-white shadow-xl rounded-xl border border-gray-100 py-2 min-w-[140px] z-[100]"
            >
              {languages.map((l) => (
                <li
                  key={l.id}
                  role="option"
                  aria-selected={activeLangId === l.id}
                  onClick={() => switchLanguage(l.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); switchLanguage(l.id); } }}
                  tabIndex={0}
                  className={`px-4 py-2 text-sm font-bold cursor-pointer hover:bg-gray-50 ${activeLangId === l.id ? 'text-brand-secondary' : 'text-brand-primary'}`}
                >
                  {l.name}
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // 2. MOBILE MENU DESIGN: Horizontal row
  if (variant === 'mobile') {
    return (
      <div className="flex items-center gap-4 text-lg uppercase font-bold text-brand-primary" role="group" aria-label="Select language">
        <Icon name="Globe" size={20} />
        {languages.map((lang, index) => (
          <React.Fragment key={lang.id}>
            <button
              onClick={() => switchLanguage(lang.id)}
              aria-label={`Switch to ${lang.name}`}
              aria-current={currentLangId === lang.id ? 'true' : undefined}
              className={`
                cursor-pointer transition-opacity uppercase font-bold
                ${currentLangId === lang.id ? 'opacity-100' : 'opacity-30'}
                ${index > 0 ? 'pl-4 border-l text-brand-primary' : ''}
              `}
            >
              {lang.id}
            </button>
          </React.Fragment>
        ))}
      </div>
    );
  }

  // 3. HEADER DESIGN: Dropdown
  return (
    <div className="relative border-l border-brand-primary/10 pl-6" ref={dropdownRef}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        aria-expanded={isDropdownOpen}
        aria-haspopup="listbox"
        aria-label={`Select language, current: ${currentLang?.name || currentLang?.id}`}
        className="flex items-center gap-2 cursor-pointer text-brand-primary font-bold hover:text-brand-secondary uppercase text-sm group"
      >
        <Icon name="Globe" size={16} />
        <span>{currentLang?.id}</span>
        <Icon name="ChevronDown" size={14} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isDropdownOpen && (
          <motion.ul
            role="listbox"
            aria-label="Select language"
            initial={{ opacity: 0, y: -4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.95 }}
            transition={{ duration: 0.15, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="absolute top-full right-0 mt-2 bg-white shadow-xl border border-gray-100 rounded-lg overflow-hidden min-w-[120px]"
          >
            {languages.map((lang) => (
              <li
                key={lang.id}
                role="option"
                aria-selected={currentLangId === lang.id}
                onClick={() => switchLanguage(lang.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); switchLanguage(lang.id); } }}
                tabIndex={0}
                className={`px-4 py-3 text-xs font-bold uppercase cursor-pointer hover:bg-gray-50 ${currentLangId === lang.id ? 'text-brand-secondary bg-gray-50' : 'text-brand-primary'}`}
              >
                {lang.name}
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
